import { NxErrorModule } from "@allianz/ng-aquila/base";
import { NxButtonModule } from "@allianz/ng-aquila/button";
import { NxCheckboxModule } from "@allianz/ng-aquila/checkbox";
import { NxFormfieldModule } from "@allianz/ng-aquila/formfield";
import { NxGridModule } from "@allianz/ng-aquila/grid";
import { NxIconModule } from "@allianz/ng-aquila/icon";
import { NxInputModule } from "@allianz/ng-aquila/input";
import { NxLinkModule } from "@allianz/ng-aquila/link";
import { NxMessageModule } from "@allianz/ng-aquila/message";
import { CommonModule } from "@angular/common";
import {
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { CommonModalComponent } from "@modules/modals/common-modal/common-modal.component";
import { TermsOfUseModalComponent } from "@modules/modals/terms-of-use-modal/terms-of-use-modal.component";
import { Select, Store } from "@ngxs/store";
import { CustomValidators } from "@shared/classes/custom-validator";
import {
  changePasswordLeaveModal,
  resetPasswordSuccessModalData,
} from "@shared/data/modalData";
import { NxPasswordTouchToggleDirective } from "@shared/directives/nx-password-touch-toggle.directive";
import { debounceTime } from "rxjs/operators";
import { ModalService } from "src/app/shared/services/modal.service";
import { SUBMIT_RESET_PASSWORD } from "src/app/store/login/login.action";
import {
  GET_SECRET_QUESTION,
  RESET_SECRET_QUESTION,
} from "src/app/store/user/user.action";
import { UserState } from "src/app/store/user/user.state";

import { AuthSideHeaderComponent } from "../auth-side-header/auth-side-header.component";

@Component({
  selector: "app-reset-password",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NxButtonModule,
    NxCheckboxModule,
    NxErrorModule,
    NxFormfieldModule,
    NxGridModule,
    NxIconModule,
    NxInputModule,
    NxLinkModule,
    NxMessageModule,
    AuthSideHeaderComponent,
    NxPasswordTouchToggleDirective,
  ],
  templateUrl: "./reset-password.component.html",
  styleUrl: "./reset-password.component.scss",
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  protected forgotPasswordForm!: FormGroup;
  protected searchUserId: string = "";
  protected isUserIdRequired: boolean = false;
  protected failedSearchUserId: boolean = false;
  protected resetPasswordErrorMsg: string = "";
  protected resetPasswordErrorTitle: string = "";
  protected formDirty: boolean = false;
  protected success: boolean = false;
  protected backendError: boolean = false;

  @Select(UserState.secretQuestionDescp) protected secretQuesionDesc$;
  @Select(UserState.secretQuestionDescp) protected secretQuestionDescp$;
  @Select(UserState.searchUserResetErrorMsg)
  protected searchUserResetErrorMsg$;
  @Select(UserState.searchUserResetPasswordErrorTitle)
  protected searchUserResetPasswordErrorTitle$;
  @Select(UserState.getSecretQuestionError) protected secretQuestionError$;

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly store: Store,
    private readonly modalService: ModalService,
  ) {}

  ngOnInit() {
    this.initForm();
    this.store.dispatch(new RESET_SECRET_QUESTION());
    this.onChangeForm();
    window.scroll(0, 0);
  }

  private initForm() {
    this.forgotPasswordForm = this.fb.group({
      userId: [this.searchUserId],
      secretAnswer: ["", Validators.required],
      newPassword: [
        "",
        Validators.compose([
          Validators.required,
          CustomValidators.patternValidator(/^.{15,20}$/, {
            minAndMaxNum: true,
          }),
          CustomValidators.patternValidator(/^(?=.*[a-zA-Z])(?=.*[0-9])/, {
            hasAlphanumeric: true,
          }),
          CustomValidators.patternValidator(/^(?=.*[a-z])(?=.*[A-Z])/, {
            uppercaseAndLowercase: true,
          }),
          CustomValidators.patternValidator(/[@#$^%&!*]/, {
            hasSpecialCharacters: true,
          }),
        ]),
      ],
      confirmNewPassword: [
        "",
        Validators.compose([
          Validators.required,
          CustomValidators.patternValidator(/^.{15,20}$/, {
            minAndMaxNum: true,
          }),
          CustomValidators.patternValidator(/^(?=.*[a-zA-Z])(?=.*[0-9])/, {
            hasAlphanumeric: true,
          }),
          CustomValidators.patternValidator(/^(?=.*[a-z])(?=.*[A-Z])/, {
            uppercaseAndLowercase: true,
          }),
          CustomValidators.patternValidator(/[@#$^%&!*]/, {
            hasSpecialCharacters: true,
          }),
        ]),
      ],
      acknowledgeChecked: [false, Validators.required],
    });
  }

  protected onSearch() {
    this.isUserIdRequired = false;
    this.failedSearchUserId = false;
    this.backendError = false;

    if (!this.searchUserId) {
      this.isUserIdRequired = true;
      return;
    }

    this.store
      .dispatch(new GET_SECRET_QUESTION(this.searchUserId))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const userState = this.store.selectSnapshot<any>(
          (state) => state.UserState,
        );
        if (userState.secretQuestionDescp == null) {
          this.failedSearchUserId = true;
          return;
        }
        this.failedSearchUserId = false;
      });

    this.forgotPasswordForm.controls["userId"].setValue(this.searchUserId);
    this.formDirty = true;
  }

  protected onSubmit() {
    if (
      this.forgotPasswordForm.controls["newPassword"].value !==
      this.forgotPasswordForm.controls["confirmNewPassword"].value
    ) {
      this.forgotPasswordForm.controls["confirmNewPassword"].setErrors({
        mustMatch: true,
      });
      return;
    }

    const userState = this.store.selectSnapshot<any>(
      (state) => state.UserState,
    );
    const payload = {
      secretQuestion: userState.secretQuestionNo,
      secretQuestionDescp: userState.secretQuestion,
      userId: this.searchUserId,
      secretAnswer: this.forgotPasswordForm.controls["secretAnswer"].value,
      newPassword: this.forgotPasswordForm.controls["newPassword"].value,
      confirmedNewPassword:
        this.forgotPasswordForm.controls["confirmNewPassword"].value,
      termsOfUse: this.forgotPasswordForm.controls["acknowledgeChecked"].value,
    };

    this.store
      .dispatch(new SUBMIT_RESET_PASSWORD(payload))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const loginState = this.store.selectSnapshot<any>(
          (state) => state.LoginState,
        );
        if (loginState.isResetPasswordSuccess) {
          this.success = true;
          this.backendError = false;
          this.resetPasswordErrorMsg = "";
          this.resetPasswordErrorTitle = "";
          this.modalService.openDialogWithCallbackHandler(
            CommonModalComponent,
            resetPasswordSuccessModalData,
            (result) => {
              if (result) {
                this.navigateToLogin();
              }
            },
          );
          return;
        }

        this.success = false;
        this.backendError = true;
        this.resetPasswordErrorMsg = loginState.resetPasswordErrorMsg;
        this.resetPasswordErrorTitle = loginState.resetPasswordErrorTitle;
      });
  }

  protected toggleTermOfUse() {
    this.modalService.openDialog(TermsOfUseModalComponent);
  }

  protected toLeave() {
    if (this.formDirty && !this.success) {
      this.modalService.openDialogWithCallbackHandler(
        CommonModalComponent,
        changePasswordLeaveModal,
        (result) => {
          if (result) {
            this.navigateToLogin();
          }
        },
      );
      return;
    }

    this.navigateToLogin();
  }

  private navigateToLogin() {
    this.router.navigate(["/login"]);
  }

  private onChangeForm() {
    this.forgotPasswordForm
      .get("secretAnswer")
      .valueChanges.pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.formDirty = this.forgotPasswordForm.dirty;
        this.backendError = false;
      });

    this.forgotPasswordForm
      .get("newPassword")
      .valueChanges.pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.formDirty = this.forgotPasswordForm.dirty;
        this.backendError = false;
      });

    this.forgotPasswordForm
      .get("confirmNewPassword")
      .valueChanges.pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.formDirty = this.forgotPasswordForm.dirty;
        this.backendError = false;
      });
  }

  ngOnDestroy() {
    this.store.dispatch(new RESET_SECRET_QUESTION());
  }
}
